import { useEffect, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MenuCloseIcon, MenuImageAddIcon } from '@noor/ui';
import { MAX_IMAGE_BYTES, MAX_IMAGE_LABEL, toast } from '@noor/lib';
import type { BranchFormPhoto } from '../model/schema';

interface PhotoFieldMobileProps {
  /** Form field this tile owns — `icon` or `backgroundImage`. */
  name: 'icon' | 'backgroundImage';
  /** The prompt INSIDE the tile — «Загрузить фото» / «Загрузить задний фон». */
  uploadLabel: string;
  /** Accessible name, since the mobile tile carries no label above it. */
  label: string;
  /** Format requirements, surfaced as the tile's tooltip. */
  hint: string;
  removeLabel: string;
  /** Width class — the design gives the two tiles different widths. */
  className?: string;
}

/**
 * One of the branch's two photos on mobile (Figma nodes 26614:20475 icon /
 * 26614:20479 background).
 *
 * Three differences from the desktop `PhotoField`, all from the design:
 *
 * - **No label above the tile.** The tile's own prompt names it («Загрузить
 *   фото» / «Загрузить задний фон»), so a caption above would say it twice on
 *   a screen with no room for it. The name survives as the button's
 *   `aria-label`.
 * - **A SOLID grey tile, not a dashed drop zone.** Dropping is a
 *   pointer-device affordance; on a phone the tile is only ever tapped.
 * - **106px tall with a 16px radius**, against desktop's 139px / 24px.
 */
export function PhotoFieldMobile({
  name,
  uploadLabel,
  label,
  hint,
  removeLabel,
  className,
}: PhotoFieldMobileProps) {
  const { t } = useTranslation('eats');
  const { control, setValue } = useFormContext();
  const { field, fieldState } = useController({ control, name });
  const photo = field.value as BranchFormPhoto | undefined;

  const inputRef = useRef<HTMLInputElement>(null);
  // Every object URL this field created, revoked together on unmount. Revoking
  // eagerly on replace would blank the tile still rendering that exact URL.
  const createdUrls = useRef<Set<string>>(new Set());
  useEffect(() => {
    const created = createdUrls.current;
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
      created.clear();
    };
  }, []);

  function pick(files: FileList | null): void {
    const file = files?.[0];
    if (!file) return;
    // Rejected by NAME rather than dropped silently — a photo that vanishes on
    // selection reads as a broken uploader. The input is cleared either way, so
    // re-picking the same file after shrinking it still fires `change`.
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(
        t('menu.product.form.photoTooLarge', {
          name: file.name,
          limit: MAX_IMAGE_LABEL,
        }),
      );
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const url = URL.createObjectURL(file);
    createdUrls.current.add(url);
    field.onChange({ url, file } satisfies BranchFormPhoto);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className={`relative h-[106px] ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        title={hint}
        aria-label={label}
        className="size-full overflow-hidden rounded-2xl bg-muted transition-colors outline-none hover:cursor-pointer focus-visible:ring-[3px] focus-visible:ring-ring/40 active:bg-muted/70"
      >
        {photo ? (
          <img src={photo.url} alt="" className="size-full object-cover" />
        ) : (
          /* 9px between glyph and prompt (Figma «Image upload content»). */
          <span className="flex size-full flex-col items-center justify-center gap-[9px] px-2 text-foreground">
            <MenuImageAddIcon className="h-5 w-[22px]" />
            <span className="text-center text-xs font-medium">{uploadLabel}</span>
          </span>
        )}
      </button>

      {photo && (
        <button
          type="button"
          onClick={() =>
            setValue(name, undefined, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            })
          }
          aria-label={removeLabel}
          className="absolute -top-1.5 -right-2 flex size-6 items-center justify-center rounded-full bg-card text-foreground shadow-[0_0_16px_rgba(0,0,0,0.12)] transition-colors outline-none hover:cursor-pointer focus-visible:ring-[3px] focus-visible:ring-ring/40 active:bg-muted"
        >
          <MenuCloseIcon className="size-[18px]" />
        </button>
      )}

      {fieldState.error?.message && (
        <span role="alert" className="absolute -bottom-5 left-0 text-xs text-destructive">
          {fieldState.error.message}
        </span>
      )}

      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept="image/jpeg,image/jpg"
        onChange={(e) => pick(e.target.files)}
      />
    </div>
  );
}
